import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useSubscriptionsList } from "./use-subscriptions-list";
import { SubscriptionsFilters } from "./SubscriptionsFilters";
import { SubscriptionsTable } from "./SubscriptionsTable";
import { SubscriptionsPagination } from "./SubscriptionsPagination";
import {
  SUBSCRIPTIONS_PAGE_SIZE,
  SUBSCRIPTIONS_SEARCH_DEBOUNCE_MS,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "./constants";

export function SubscriptionsPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [tier, setTier] = useState<SubscriptionTier | null>(null);
  const [page, setPage] = useState(1);

  // A narrower filter can leave the table stranded on an empty page —
  // reset to page 1 whenever the result set changes.
  useEffect(() => {
    setPage(1);
  }, [query, status, tier]);

  const debouncedQuery = useDebouncedValue(query, SUBSCRIPTIONS_SEARCH_DEBOUNCE_MS);
  const { data, isLoading, isFetching, isError } = useSubscriptionsList({
    query: debouncedQuery,
    status,
    tier,
    page,
  });
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasFilters = debouncedQuery.trim().length > 0 || status !== null || tier !== null;

  const handleReset = () => {
    setQuery("");
    setStatus(null);
    setTier(null);
  };

  return (
    <div data-testid="subscriptions-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("subscriptions.list.eyebrow")}</p>
        <h1 className="text-4xl">{t("subscriptions.list.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("subscriptions.list.subtitle")}
        </p>
      </header>

      <SubscriptionsFilters
        query={query}
        status={status}
        tier={tier}
        onQueryChange={setQuery}
        onStatusChange={setStatus}
        onTierChange={setTier}
        onReset={handleReset}
      />

      {isLoading && (
        <p data-testid="subscriptions-loading" className="font-sans text-sm text-muted-foreground">
          {t("subscriptions.list.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="subscriptions-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("subscriptions.list.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="subscriptions-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("subscriptions.list.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {hasFilters ? t("subscriptions.list.emptyFiltered") : t("subscriptions.list.emptyBody")}
          </p>
        </div>
      )}

      {!isError && items.length > 0 && (
        <div
          data-testid="subscriptions-list"
          aria-busy={isFetching}
          className={isFetching ? "space-y-4 opacity-70 transition-opacity" : "space-y-4"}
        >
          <SubscriptionsTable items={items} />
          <SubscriptionsPagination
            page={page}
            limit={SUBSCRIPTIONS_PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
